/**
 * dialogueMachine — moteur de dialogue guidé, réducteur PUR (US-VDPI-03 AC1).
 *
 * Implémente les 7 états du plan §5 :
 *   IDLE → SELECT_ROOM → {CONFIRM_PATIENT | SELECT_PATIENT} → MENU
 *        → AWAIT_ACTION → CAPTURE → AWAIT_COMMIT
 *   + transitions `corrige` / `annule`
 *   + chaînage post-commit (retour MENU même patient)
 *
 * Pur, sans dépendance DOM ni réseau : le réducteur calcule le prochain état
 * et les EFFETS à exécuter (parole, appels backend, earcons, re-arm wake).
 * L'orchestrateur (hook React) exécute ces effets et renvoie les résultats
 * sous forme de nouveaux events (DATA_*).
 *
 * Réf : docs/meta/plan/PLAN-VOCAL-DPI-JSON-2026-06-17.md §5.
 */

import type { ChambreOut, PatientLettre, MenuItem } from '@/shared/types/dpi';
import type { ControlCommand } from './parseControl';
import type { IntentName } from './parseIntent';
import type { EarconSound } from './earcons';
import {
  buildData,
  captureModeFor,
  concordanceConflict,
  constanteAlert,
  guidedFields,
  missingField,
  readback,
} from './captureSpec';

export type DialogueStateName =
  | 'IDLE'
  | 'SELECT_ROOM'
  | 'CONFIRM_PATIENT'
  | 'SELECT_PATIENT'
  | 'MENU'
  | 'AWAIT_ACTION'
  | 'CAPTURE'
  | 'AWAIT_COMMIT';

/** 4 états visuels de l'orbe (AC5) dérivés de l'état logique. */
export type OrbeState = 'idle' | 'listening' | 'capturing' | 'confirming';

export interface DialogueContext {
  /** Chambre sélectionnée (résultat GET /chambres/{n}). */
  chambre: ChambreOut | null;
  /** Patient courant (après confirmation / désambiguïsation). */
  patient: PatientLettre | null;
  /** Menu lettré du patient (résultat GET /patient/{id}/menu). */
  menu: MenuItem[];
  /** Item d'action en cours de capture (AWAIT_ACTION → CAPTURE). */
  action: MenuItem | null;
  /** Texte dicté capturé (dictée narrative : CAPTURE → AWAIT_COMMIT). */
  captured: string;
  /** Valeurs des champs guidés collectées (constantes / score). */
  fields: Record<string, string>;
  /** Index du champ guidé en cours (capture guidée champ-par-champ). */
  fieldIndex: number;
  /** Sous-vue du hub (AWAIT_ACTION) : liste des prescriptions, ou aucune (US-VDPI-09). */
  subView: 'prescriptions' | null;
  /** Relecture de la dernière saisie enregistrée (intent « relire », read-only). */
  lastReadback: string | null;
}

export interface DialogueState {
  name: DialogueStateName;
  ctx: DialogueContext;
  /** Dernier message système destiné à `speak()` + ligne UI (echo). */
  prompt: string;
}

// ─── Effets (exécutés par l'orchestrateur, hors réducteur) ──────────────────

export type DialogueEffect =
  | { type: 'speak'; text: string }
  | { type: 'earcon'; sound: EarconSound }
  | { type: 'fetchChambre'; numero: string }
  | { type: 'fetchMenu'; patientId: string }
  | { type: 'commit'; item: MenuItem; patient: PatientLettre; chambre: string; data: Record<string, unknown> }
  | { type: 'rearmWake' };

// ─── Events (entrées du réducteur) ──────────────────────────────────────────

export type DialogueEvent =
  /** Wake "saisi … chambre N … stop" détecté → numéro extrait. */
  | { type: 'WAKE_ROOM'; numero: string }
  /** Réponse backend GET /chambres/{n}. */
  | { type: 'DATA_CHAMBRE'; chambre: ChambreOut }
  | { type: 'DATA_CHAMBRE_NOT_FOUND'; numero: string }
  /** Réponse backend GET /patient/{id}/menu. */
  | { type: 'DATA_MENU'; menu: MenuItem[] }
  /** Transcription utilisateur (commande de contrôle déjà parsée). */
  | { type: 'CONTROL'; command: ControlCommand }
  /** Sélection par NUMÉRO (position 1-based) — patient (SELECT_PATIENT) ou action (AWAIT_ACTION). */
  | { type: 'SELECT'; numero: number | null }
  /** Intention vocale reconnue (parseIntent) — routage depuis le hub AWAIT_ACTION. */
  | { type: 'INTENT'; intent: IntentName; numero?: number }
  /** « stop » = turn OFF (Option 1) : ferme le flux vocal → retour ARMÉ, silencieux. */
  | { type: 'STOP' }
  /** Dictée libre capturée (texte d'action — sections narratives). */
  | { type: 'CAPTURE_TEXT'; text: string }
  /** Réponse à un champ guidé (constantes / score). */
  | { type: 'CAPTURE_FIELD'; value: string }
  /** Confirmation backend d'écriture (POST /saisie OK). */
  | { type: 'COMMIT_OK' };

export interface Transition {
  state: DialogueState;
  effects: DialogueEffect[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const emptyCtx: DialogueContext = {
  chambre: null,
  patient: null,
  menu: [],
  action: null,
  captured: '',
  fields: {},
  fieldIndex: 0,
  subView: null,
  lastReadback: null,
};

export const initialState: DialogueState = {
  name: 'IDLE',
  ctx: emptyCtx,
  prompt: '',
};

/** Mapping état logique → état visuel orbe (AC5). */
export function orbeStateFor(name: DialogueStateName): OrbeState {
  switch (name) {
    case 'IDLE':
      return 'idle';
    case 'SELECT_ROOM':
    case 'CONFIRM_PATIENT':
    case 'SELECT_PATIENT':
    case 'MENU':
    case 'AWAIT_ACTION':
      return 'listening';
    case 'CAPTURE':
      return 'capturing';
    case 'AWAIT_COMMIT':
      return 'confirming';
  }
}

function speak(text: string): DialogueEffect[] {
  return [{ type: 'speak', text }];
}

function go(
  name: DialogueStateName,
  ctx: DialogueContext,
  prompt: string,
  effects: DialogueEffect[] = [],
): Transition {
  return { state: { name, ctx, prompt }, effects: [...speak(prompt), ...effects] };
}

/**
 * « stop » = turn OFF (Option 1) : ferme le flux vocal → retour ARMÉ.
 * SILENCIEUX (pas de TTS — on coupe la voix) ; earcon de clôture + re-arm wake.
 * Le traitement async déjà lancé (commit) n'est pas touché ici : ses résultats
 * tardifs sont ignorés par les gardes d'état du réducteur.
 */
function stopOff(): Transition {
  return {
    state: { name: 'IDLE', ctx: emptyCtx, prompt: '' },
    effects: [
      { type: 'earcon', sound: 'stop' },
      { type: 'rearmWake' },
    ],
  };
}

/** Retour IDLE + earcon clôture + re-arm wake (annulation / fin). */
function abort(prompt: string): Transition {
  return {
    state: { name: 'IDLE', ctx: emptyCtx, prompt },
    effects: [
      { type: 'speak', text: prompt },
      { type: 'earcon', sound: 'stop' },
      { type: 'rearmWake' },
    ],
  };
}

function patientLabel(p: PatientLettre): string {
  return `${p.prenom} ${p.nom}`;
}

/**
 * Entrée en capture pour une action choisie (NQ-C, plan §6).
 *   - guided      → demande le 1er champ, reste en CAPTURE (champ-par-champ)
 *   - confirm     → rien à dicter (administration pré-remplie) → readback → AWAIT_COMMIT
 *   - dictation   → écoute libre (transmission / refus)
 */
function enterCapture(ctx: DialogueContext, item: MenuItem): Transition {
  const base = { ...ctx, action: item, captured: '', fields: {}, fieldIndex: 0 };
  const mode = captureModeFor(item.section);

  if (mode === 'guided') {
    const fields = guidedFields(item.section);
    return go('CAPTURE', base, `${item.label}. ${fields[0]?.prompt ?? 'Valeur ?'}`);
  }
  if (mode === 'confirm') {
    // Rien à dicter : relecture directe, attente de validation.
    return go('AWAIT_COMMIT', base, readback(item.section, item, {}, ''));
  }
  // dictation narrative
  return go('CAPTURE', base, `${item.label}. Je vous écoute.`);
}

/** Relecture déterministe + passage en AWAIT_COMMIT (capture terminée). */
function toCommit(ctx: DialogueContext): Transition {
  const item = ctx.action!;
  const phrase = readback(item.section, item, ctx.fields, ctx.captured);
  return go(
    'AWAIT_COMMIT',
    ctx,
    `${phrase} Dites validation pour enregistrer, corrige ou annule.`,
  );
}

// ─── Réducteur ───────────────────────────────────────────────────────────────

export function dialogueReducer(state: DialogueState, event: DialogueEvent): Transition {
  const { name, ctx } = state;

  switch (event.type) {
    // ── Réveil : "saisi chambre N stop" (depuis n'importe quel état) ─────────
    case 'WAKE_ROOM': {
      // L'earcon d'ouverture est joué à la DÉTECTION wake (onWakeWord), pas ici
      // (plan §3.3 B3) : à ce stade la capture est déjà terminée.
      const prompt = `Chambre ${event.numero}, recherche en cours.`;
      return {
        state: { name: 'SELECT_ROOM', ctx: { ...emptyCtx }, prompt },
        effects: [
          { type: 'fetchChambre', numero: event.numero },
        ],
      };
    }

    // ── SELECT_ROOM : réponse backend ────────────────────────────────────────
    case 'DATA_CHAMBRE_NOT_FOUND': {
      if (name !== 'SELECT_ROOM') return { state, effects: [] };
      return abort(`La chambre ${event.numero} n'existe pas.`);
    }

    case 'DATA_CHAMBRE': {
      if (name !== 'SELECT_ROOM') return { state, effects: [] };
      const ch = event.chambre;
      const newCtx = { ...emptyCtx, chambre: ch };

      if (ch.patients.length === 0) {
        return abort(`La chambre ${ch.numero} est vide.`);
      }
      if (ch.patients.length === 1) {
        const p = ch.patients[0];
        return go(
          'CONFIRM_PATIENT',
          { ...newCtx, patient: p },
          `Chambre ${ch.numero}, ${patientLabel(p)}. Dites validation.`,
        );
      }
      // ≥2 patients → désambiguïsation par NUMÉRO (position, E4)
      const liste = ch.patients.map((p, i) => `patient ${i + 1}, ${p.nom}`).join('. ');
      return go(
        'SELECT_PATIENT',
        newCtx,
        `Chambre ${ch.numero} : ${liste}. Dites le numéro du patient.`,
      );
    }

    // ── DATA_MENU : entrée dans MENU → AWAIT_ACTION ──────────────────────────
    case 'DATA_MENU': {
      if (name !== 'MENU') return { state, effects: [] };
      // Hub léger (AC2) : plus d'énumération vocale — l'écran liste les mots-clés.
      const newCtx = { ...ctx, menu: event.menu, subView: null };
      return go('AWAIT_ACTION', newCtx, 'Que faire ?');
    }

    // ── COMMIT_OK : écriture confirmée → chaînage MENU même patient ──────────
    case 'COMMIT_OK': {
      if (name !== 'AWAIT_COMMIT') return { state, effects: [] };
      // Chaînage allégé (AC4) : « Enregistré. » + retour hub SILENCIEUX (plus de
      // relecture du menu). Mémorise la relecture pour l'intent « relire ».
      const last = ctx.action
        ? readback(ctx.action.section, ctx.action, ctx.fields, ctx.captured)
        : ctx.lastReadback;
      const hubCtx = {
        ...ctx, action: null, captured: '', fields: {}, fieldIndex: 0,
        subView: null, lastReadback: last,
      };
      return {
        state: { name: 'AWAIT_ACTION', ctx: hubCtx, prompt: 'Enregistré.' },
        effects: [
          { type: 'earcon', sound: 'success' },
          { type: 'speak', text: 'Enregistré.' },
        ],
      };
    }

    // ── Dictée libre capturée (narratif) → concordance, relecture, commit ────
    case 'CAPTURE_TEXT': {
      if (name !== 'CAPTURE') return { state, effects: [] };
      // Garde concordance (AC5 #10) : la dictée nomme un autre patient de la chambre.
      if (ctx.patient && ctx.chambre) {
        const conflit = concordanceConflict(event.text, ctx.patient, ctx.chambre.patients);
        if (conflit) {
          return go(
            'CAPTURE',
            { ...ctx, captured: '' },
            `Concordance refusée : ${conflit} ne correspond pas au dossier ouvert. Rien n'a été écrit. Reprenez ou annulez.`,
          );
        }
      }
      const newCtx = { ...ctx, captured: event.text };
      return toCommit(newCtx);
    }

    // ── Réponse à un champ guidé (constantes / score) ────────────────────────
    case 'CAPTURE_FIELD': {
      if (name !== 'CAPTURE' || !ctx.action) return { state, effects: [] };
      const section = ctx.action.section;
      const fields = guidedFields(section);
      const cur = fields[ctx.fieldIndex];
      if (!cur) return { state, effects: [] };

      const value = event.value.trim();
      if (!value) {
        // Pas de valeur → redemande le même champ (complétude #8).
        return go('CAPTURE', ctx, cur.prompt);
      }

      const newFields = { ...ctx.fields, [cur.key]: value };
      const effects: DialogueEffect[] = [];

      // Garde-fou bornes critiques constantes (#13).
      if (section === 'constantes') {
        const alert = constanteAlert(cur.key, value);
        if (alert) effects.push({ type: 'speak', text: alert });
      }

      const nextIdx = ctx.fieldIndex + 1;
      const nextField = fields[nextIdx];
      if (nextField) {
        // Champ suivant.
        const t = go('CAPTURE', { ...ctx, fields: newFields, fieldIndex: nextIdx }, nextField.prompt);
        return { state: t.state, effects: [...effects, ...t.effects] };
      }

      // Tous les champs renseignés → complétude (pas de score partiel #8).
      const filledCtx = { ...ctx, fields: newFields, fieldIndex: nextIdx };
      const missing = missingField(section, newFields);
      if (missing) {
        const t = go('CAPTURE', filledCtx, `Il manque : ${missing.prompt}`);
        return { state: t.state, effects: [...effects, ...t.effects] };
      }
      const t = toCommit(filledCtx);
      return { state: t.state, effects: [...effects, ...t.effects] };
    }

    // ── Mots de contrôle ──────────────────────────────────────────────────────
    case 'CONTROL':
      return reduceControl(state, event.command);

    // ── Sélection par numéro (patient / action) ──────────────────────────────
    case 'SELECT':
      return reduceSelect(state, event.numero);

    // ── Intention vocale (hub AWAIT_ACTION) ──────────────────────────────────
    case 'INTENT':
      return reduceIntent(state, event.intent);

    // ── « stop » = fermeture du flux vocal → ARMÉ (no-op si déjà IDLE) ────────
    case 'STOP':
      return name === 'IDLE' ? { state, effects: [] } : stopOff();
  }
}

/**
 * Sélection par NUMÉRO (E4, spec §3/§6/§9). Le numéro = POSITION 1-based dans la
 * liste affichée/annoncée. Hors plage ou null → re-prompt (jamais de lien
 * hasardeux). Réutilise `parseSpokenInt` côté orchestrateur (DRY).
 */
function reduceSelect(state: DialogueState, numero: number | null): Transition {
  const { name, ctx } = state;

  switch (name) {
    case 'SELECT_PATIENT': {
      const p = numero !== null ? ctx.chambre?.patients[numero - 1] : undefined;
      if (p) {
        return {
          state: { name: 'MENU', ctx: { ...ctx, patient: p }, prompt: `${patientLabel(p)}. Chargement du menu.` },
          effects: [
            { type: 'speak', text: `${patientLabel(p)}. Chargement du menu.` },
            { type: 'fetchMenu', patientId: p.patient_id },
          ],
        };
      }
      return go('SELECT_PATIENT', ctx, 'Numéro de patient inconnu. Dites le numéro du patient.');
    }

    case 'AWAIT_ACTION': {
      const item = numero !== null ? ctx.menu[numero - 1] : undefined;
      if (item) return enterCapture(ctx, item);
      return go('AWAIT_ACTION', ctx, "Numéro d'action inconnu. Dites le numéro de l'action.");
    }

    default:
      return { state, effects: [] };
  }
}

/** Item de menu synthétique : route un intent vers une capture de section (US-VDPI-09). */
function synthItem(section: string): MenuItem {
  return { lettre: '', section, label: section };
}

/**
 * Route une INTENTION (parseIntent) depuis le hub AWAIT_ACTION (US-VDPI-09 AC3).
 *   - constantes/score/transmission/refus/surveillance → enterCapture (item synthétique)
 *   - prescriptions → sous-liste (subView) + annonce courte, reste AWAIT_ACTION
 *   - relire → relecture de la dernière saisie (read-only), reste AWAIT_ACTION
 *   - administrer / administrer_autre → sous-scénario Prescriptions = US-VDPI-10 (V1)
 */
function reduceIntent(state: DialogueState, intent: IntentName): Transition {
  const { name, ctx } = state;
  if (name !== 'AWAIT_ACTION') return { state, effects: [] };

  switch (intent) {
    case 'prescriptions': {
      const admins = ctx.menu.filter((m) => m.section === 'administration');
      const liste = admins.length
        ? admins.map((m, i) => `${i + 1}, ${m.medicament ?? m.label} ${m.dose ?? ''}`.trim()).join('. ')
        : 'Aucune prescription.';
      return go('AWAIT_ACTION', { ...ctx, subView: 'prescriptions' }, liste);
    }
    case 'relire':
      return go('AWAIT_ACTION', ctx, ctx.lastReadback ?? 'Aucune saisie à relire.');
    case 'constantes':
    case 'score':
    case 'transmission':
    case 'refus':
    case 'surveillance':
      return enterCapture({ ...ctx, subView: null }, synthItem(intent));
    default:
      return { state, effects: [] };
  }
}

function reduceControl(state: DialogueState, cmd: ControlCommand): Transition {
  const { name, ctx } = state;

  // `annule` est universel : retour IDLE depuis tout état actif.
  if (cmd.kind === 'cancel') {
    if (name === 'IDLE') return { state, effects: [] };
    return abort('Annulé.');
  }

  switch (name) {
    // ── CONFIRM_PATIENT : "validation" → MENU ─────────────────────────────────
    case 'CONFIRM_PATIENT': {
      if (cmd.kind === 'validate' && ctx.patient) {
        return {
          state: { name: 'MENU', ctx, prompt: 'Chargement du menu.' },
          effects: [
            { type: 'speak', text: 'Chargement du menu.' },
            { type: 'fetchMenu', patientId: ctx.patient.patient_id },
          ],
        };
      }
      return go('CONFIRM_PATIENT', ctx, 'Dites validation pour confirmer le patient, ou annule.');
    }

    // Sélection patient (SELECT_PATIENT) et action (AWAIT_ACTION) = 100 % par
    // NUMÉRO depuis E4 → gérées par l'event SELECT (reduceSelect), plus par les
    // mots de contrôle. `annule` (cancel) reste pris en charge en tête de fonction.

    // ── AWAIT_COMMIT : validation / corrige / annule ──────────────────────────
    case 'AWAIT_COMMIT': {
      if (cmd.kind === 'validate') {
        if (ctx.action && ctx.patient && ctx.chambre) {
          return {
            state,
            effects: [
              {
                type: 'commit',
                item: ctx.action,
                patient: ctx.patient,
                chambre: ctx.chambre.numero,
                data: buildData(ctx.action.section, ctx.action, ctx.fields, ctx.captured),
              },
            ],
          };
        }
        return { state, effects: [] };
      }
      if (cmd.kind === 'correct') {
        // Réénonce : retour CAPTURE même action (réinitialise champs + dictée).
        return ctx.action
          ? enterCapture(ctx, ctx.action)
          : go('CAPTURE', { ...ctx, captured: '', fields: {}, fieldIndex: 0 }, 'Reprenez, je vous écoute.');
      }
      return go('AWAIT_COMMIT', ctx, 'Dites validation, corrige ou annule.');
    }

    // Autres états : commande ignorée.
    default:
      return { state, effects: [] };
  }
}
